import {
  useCallback,
  createContext,
  useContext,
  ReactElement,
  HTMLAttributes,
  useMemo,
  ContextType,
  MouseEvent,
  ThHTMLAttributes,
  TdHTMLAttributes,
  ReactNode,
  MouseEventHandler,
  TableHTMLAttributes,
} from 'react';

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  Table as ReactTable,
  ColumnDef,
  Row,
  TableOptions,
  Header,
  Cell,
  Column,
} from '@tanstack/react-table';
import { twMerge } from 'tailwind-merge';

import { Button } from '../Button';
import { Icon } from '../Icon';
import { Shimmer } from '../Shimmer';
import { Text } from '../Text';

import { getColumn } from './utils';

interface TableCellHeaderProps<T> extends ThHTMLAttributes<HTMLTableCellElement> {
  readonly as: 'th';
  readonly value?: Header<T, unknown>;
  readonly column?: Column<T, unknown>;
}

interface TableCellBodyProps<T> extends TdHTMLAttributes<HTMLTableCellElement> {
  readonly as?: 'td';
  readonly value?: Cell<T, unknown>;
  readonly column?: Column<T, unknown>;
}

type TableCellProps<T> = TableCellHeaderProps<T> | TableCellBodyProps<T>;

interface TableRowProps<T> extends Omit<HTMLAttributes<HTMLTableRowElement>, 'onClick'> {
  readonly id?: string;
  readonly isRowHighlighted?: (row: Row<T>) => boolean;
  readonly onClick?: (row: T, e: MouseEvent<HTMLTableRowElement>) => void;
}

interface TableSectionProps<T> extends HTMLAttributes<HTMLTableSectionElement> {
  readonly as?: 'header' | 'footer' | 'body';
  readonly children?: ReactElement<TableRowProps<T>> | ReactElement<TableRowProps<T>>[] | ReactNode;
}

interface TableProps<T> extends HTMLAttributes<HTMLTableElement> {
  readonly data?: T[];
  readonly columns?: ColumnDef<T>[];
  readonly bordered?: boolean;
  readonly rounded?: boolean;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly options?: Partial<TableOptions<T>>;
  readonly children?: ReactElement<TableSectionProps<T>> | ReactElement<TableSectionProps<T>>[] | ReactNode;
  readonly className?: string;
}

interface DefaultTableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {
  readonly rounded?: boolean;
}

interface DefaultTableBodyProps<T> extends HTMLAttributes<HTMLTableSectionElement> {
  readonly rounded?: boolean;
  readonly onRowClick?: (row: T) => void;
  readonly isRowHighlighted?: (row: Row<T>) => boolean;
  readonly noDataPlaceholder?: string | React.ReactNode;
}

interface DefaultTableErrorProps extends HTMLAttributes<HTMLTableSectionElement> {
  readonly rounded?: boolean;
  readonly span?: number;
  readonly onRetry?: MouseEventHandler<HTMLButtonElement>;
}

interface DefaultTableProps<T> extends Omit<TableHTMLAttributes<HTMLTableElement>, 'className' | 'children'>, TableProps<T> {
  readonly hideHeader?: boolean;
  readonly onRowClick?: (row: T) => void;
  readonly isRowHighlighted?: (row: Row<T>) => boolean;
}

const SIZE_STYLE = {
  sm: 'py-[14px] text-sm',
  md: 'py-[14px]',
  lg: 'py-[18px]',
};

const CONFIG: Partial<TableOptions<unknown>> = {
  defaultColumn: {
    size: 150,
  },
};

const TableContext = createContext<{
  table: ReactTable<unknown>;
  bordered: boolean;
  rounded: boolean;
  size: keyof typeof SIZE_STYLE;
  section?: 'body' | 'header' | 'footer';
} | null>(null);

function useTableContext<T>() {
  return useContext(
    TableContext as React.Context<{
      table: ReactTable<T>;
      bordered: boolean;
      rounded: boolean;
      size: keyof typeof SIZE_STYLE;
      section?: 'body' | 'header' | 'footer';
    } | null>,
  );
}

const DEFAULT_DATA: unknown[] = [];
const DEFAULT_COLUMNS: unknown[] = [];

function Table<T>({
  data = DEFAULT_DATA as T[],
  columns = DEFAULT_COLUMNS as ColumnDef<T>[],
  children,
  bordered,
  rounded,
  size = 'md',
  options,
  className,
  ...props
}: TableProps<T>) {
  const tableConfig: ReactTable<T> = useReactTable({
    data,
    columns,
    ...(CONFIG as unknown as Partial<TableOptions<T>>),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...options,
  });

  const config = useMemo(() => {
    return {
      table: tableConfig,
      bordered: !!bordered,
      rounded: !!rounded,
      size,
    };
  }, [tableConfig, bordered, rounded, size]);

  return (
    <table {...props} className={twMerge('h-full w-full min-w-full border-collapse border-spacing-0 bg-white', className)}>
      <TableContext.Provider value={config as ContextType<typeof TableContext>}>{children}</TableContext.Provider>
    </table>
  );
}

function TableSection<T>({ as = 'body', children, className, ...props }: TableSectionProps<T>) {
  const context = useTableContext<T>();
  const contextForSection = useMemo(() => {
    return context
      ? {
          ...(context as Exclude<ContextType<typeof TableContext>, null>),
          section: as,
        }
      : null;
  }, [context, as]);

  if (!context) {
    return null;
  }

  if (as === 'header') {
    return (
      <TableContext.Provider value={contextForSection}>
        <thead className={className} {...props}>
          {children}
        </thead>
      </TableContext.Provider>
    );
  }

  if (as === 'footer') {
    return (
      <TableContext.Provider value={contextForSection}>
        <tfoot className={className} {...props}>
          {children}
        </tfoot>
      </TableContext.Provider>
    );
  }

  return (
    <TableContext.Provider value={contextForSection}>
      <tbody className={className} {...props}>
        {children}
      </tbody>
    </TableContext.Provider>
  );
}

function TableRow<T>({ id, isRowHighlighted, onClick, children, className, ...props }: TableRowProps<T>) {
  const context = useTableContext<T>();

  const row = !!id && !!context && context.table.getRow(id);
  const isSelected = (row && isRowHighlighted?.(row)) || false;

  const onRowClick = useCallback(
    (e: MouseEvent<HTMLTableRowElement>) => {
      if (row) {
        onClick?.(row.original, e);
      }
    },
    [row, onClick],
  );

  if (!context) {
    return null;
  }

  return (
    <tr
      key={id}
      data-id={id}
      aria-selected={isSelected}
      className={twMerge(
        'items-center text-neutral-700',
        context.bordered && 'border-b border-neutral-200',
        context.section === 'body' && 'hover:bg-neutral-50',
        isSelected ? 'bg-light-blue-50 hover:bg-light-blue-100' : '',
        onClick ? 'cursor-pointer' : '',
        className,
      )}
      onClick={onRowClick}
      {...props}
    >
      {children}
    </tr>
  );
}

function TableCell<T>({ as, value, column, children, className, style, colSpan, ...props }: TableCellProps<T>) {
  const context = useTableContext<T>();

  if (!context) {
    return null;
  }

  const _column = getColumn(value, column);

  if (as === 'th') {
    const content = children ?? (value && flexRender(_column?.columnDef.header, value.getContext()));

    return (
      <th
        className={twMerge(
          'px-4 text-left font-semibold text-neutral-700',
          context.rounded && 'bg-neutral-100 first:rounded-tl-xl last:rounded-tr-xl',
          SIZE_STYLE[context.size],
          _column?.columnDef.meta?.className,
          className,
        )}
        style={{ ...(_column ? { width: _column.getSize() * (colSpan ?? 1) } : undefined), ...style }}
        colSpan={colSpan}
        {...props}
      >
        {content}
      </th>
    );
  }

  const content = children ?? (value && flexRender(value.column.columnDef.cell, value.getContext()));

  return (
    <td
      className={twMerge('truncate px-4 text-left', SIZE_STYLE[context.size], _column?.columnDef.meta?.className, className)}
      style={{ ...(_column ? { width: _column.getSize() * (colSpan ?? 1) } : undefined), ...style }}
      colSpan={colSpan}
      {...props}
    >
      {content}
    </td>
  );
}

function DefaultTableHeader<T>(props: DefaultTableHeaderProps) {
  const context = useTableContext<T>();

  if (!context) {
    return null;
  }

  return (
    <TableSection as="header" {...props}>
      {context.table.getHeaderGroups().map((headerGroup) => {
        return (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableCell key={header.id} as="th" value={header} />
            ))}
          </TableRow>
        );
      })}
    </TableSection>
  );
}

function DefaultTableBody<T>({ onRowClick, isRowHighlighted, noDataPlaceholder = 'No Data Found', ...props }: DefaultTableBodyProps<T>) {
  const context = useTableContext<T>();

  if (!context) {
    return null;
  }

  const { rows } = context.table.getRowModel();
  const _span = context.table.getAllFlatColumns().length - 1;

  return (
    <TableSection {...props}>
      {rows.length === 0 ? (
        <TableRow className="w-full py-4 text-sm font-medium">
          <TableCell className="text-center" colSpan={_span}>
            {noDataPlaceholder}
          </TableCell>
        </TableRow>
      ) : null}
      {rows.map((row) => {
        const cells = row.getVisibleCells();
        const hasGroup = cells.findIndex((cell) => cell.getIsAggregated()) > -1;
        let span = cells.findIndex((cell) => !cell.getIsAggregated() || cell.renderValue() !== null);

        if (span === -1) {
          span = cells.length;
        }

        return (
          <TableRow
            key={row.id}
            id={row.id}
            isRowHighlighted={isRowHighlighted}
            onClick={onRowClick && !hasGroup ? () => onRowClick(row.original) : undefined}
          >
            {cells.map((cell, i) => {
              // eslint-disable-next-line no-nested-ternary
              return cell.getIsAggregated() && cell.renderValue() === null ? (
                i === 0 ? (
                  // Grouped cell
                  <TableCell key={cell.id} colSpan={span} value={cell} className="bg-neutral-100 font-semibold" />
                ) : null
              ) : (
                <TableCell key={cell.id} value={cell} className={hasGroup ? 'bg-neutral-100 font-semibold' : ''} />
              );
            })}
          </TableRow>
        );
      })}
    </TableSection>
  );
}

function DefaultTableLoading<T>(props: DefaultTableBodyProps<T>) {
  const context = useTableContext<T>();

  if (!context) {
    return null;
  }

  const cells = context.table.getAllFlatColumns().map((d) => {
    return !d.getIsGrouped() ? (
      <TableCell key={d.id} column={d}>
        <Shimmer size="sm" className="inline-block" />
      </TableCell>
    ) : null;
  });

  return (
    <TableSection {...props}>
      <TableRow>{cells}</TableRow>
      <TableRow>{cells}</TableRow>
      <TableRow>{cells}</TableRow>
    </TableSection>
  );
}

function DefaultTableError<T>({ onRetry, span, className, children, ...props }: DefaultTableErrorProps) {
  const context = useTableContext<T>();

  if (!context) {
    return null;
  }

  const _span = context.table.getAllFlatColumns().length - 1;

  return (
    <TableSection className={twMerge('bg-neutral-50', className)} {...props}>
      <TableRow className="py-4 text-sm font-medium">
        <TableCell colSpan={span ?? _span}>
          <div className="flex flex-col items-center justify-center p-4">
            <div className="flex items-center text-neutral-500">
              <Icon color="current" icon="maintenance" size="md" className="mr-2" />
              <Text>Oops… Something went wrong.</Text>
            </div>
            {children ? <Text className="italic text-neutral-500">({children})</Text> : null}
            <Button variant="danger" onClick={onRetry} className="mt-2" size="s">
              Retry
            </Button>
          </div>
        </TableCell>
      </TableRow>
    </TableSection>
  );
}

function DefaultTable<T>({
  hideHeader,
  data,
  columns,
  options,
  bordered,
  rounded,
  size,
  className,
  isRowHighlighted,
  onRowClick,
  children,
  ...props
}: DefaultTableProps<T>) {
  return (
    <Table
      data={data}
      columns={columns}
      bordered={bordered}
      rounded={rounded}
      className={className}
      options={options}
      size={size}
      {...props}
    >
      {hideHeader ? null : <DefaultTableHeader />}
      <DefaultTableBody isRowHighlighted={isRowHighlighted} onRowClick={onRowClick} />
      {children}
    </Table>
  );
}

Table.Section = TableSection;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.Header = DefaultTableHeader;
Table.Body = DefaultTableBody;
Table.Loading = DefaultTableLoading;
Table.Error = DefaultTableError;
Table.Default = DefaultTable;

export { Table as TableV2, useTableContext };
